<template>
  <div class="dialysis-page">
    <div v-if="sessionEnded" class="summary-section">
      <h3>Session Summary</h3>
      <p><strong>Total Elapsed Time:</strong> {{ formatTime(elapsedTime) }}</p>

      <h4>Actions Performed:</h4>
      <ul>
        <li v-for="(item, index) in activities" :key="index">{{ item }}</li>
      </ul>

      <h4>Consumables Used:</h4>
      <ul>
        <li v-for="(item, index) in consumablesUsed" :key="'c' + index">
          {{ item }}
        </li>
      </ul>
    </div>

    <div v-if="sessionStarted" class="session-summary">
      <h2>📄 Dialysis Session Summary</h2>

      <div class="section">
        <h3>Vital Signs (Pre-Session)</h3>
        <table>
          <tr>
            <th>BP (mmHg)</th>
            <th>Pulse (bpm)</th>
            <th>Resp. Rate</th>
            <th>Temp (°C)</th>
            <!-- <th>Weight (kg)</th> -->
          </tr>
          <tr v-for="vital in triage" :key="vital.id">
            <td>{{ vital.blood_pressure }}</td>
            <td>{{ vital.heart_rate }}</td>
            <td>{{ vital.respiratory_rate }}</td>
            <td>{{ vital.temperature }}</td>
            <!-- <td>{{ vital.weight }}</td> -->
          </tr>
        </table>
      </div>

      <div class="section">
        <h3>Fluid Balance Tracking</h3>
        <table>
          <tr>
            <th>Time (min)</th>
            <th>UF Rate (ml/hr)</th>
            <th>Fluid Removed (ml)</th>
            <th>Remaining (ml)</th>
            <th>Cumulative (ml)</th>
          </tr>
          <tr v-for="fluid in fluid_balance" :key="fluid.id">
            <td>{{ fluid.time_lapsed_min }}</td>
            <td>{{ fluid.ultrafiltration_rate_ml_hr }}</td>
            <td>{{ fluid.total_fluid_removed_ml }}</td>
            <td>{{ fluid.remaining_target_ml }}</td>
            <td>{{ fluid.cumulative_removed_ml }}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <h3>Machine Parameters</h3>
        <table>
          <tr>
            <th>Time (min)</th>
            <th>Blood Flow (ml/min)</th>
            <th>Dialysate Flow</th>
            <th>Venous P</th>
            <th>Arterial P</th>
            <th>TMP</th>
            <th>Conductivity</th>
            <th>Temp (°C)</th>
          </tr>
          <tr
            v-for="machine_params in dialysis_machine_parameters"
            :key="machine_params.key"
          >
            <td>{{ machine_params.minutes_lapsed }}</td>
            <td>{{ machine_params.blood_flow_rate }}</td>
            <td>{{ machine_params.dialysate_flow_rate }}</td>
            <td>{{ machine_params.venous_pressure }}</td>
            <td>{{ machine_params.arterial_pressure }}</td>
            <td>{{ machine_params.tmp }}</td>
            <td>{{ machine_params.conductivity }}</td>
            <td>{{ machine_params.dialysate_temperature }}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <h3>Complications / Events</h3>
        <table>
          <tr>
            <th>Time (min)</th>
            <th>Complication(s)</th>
            <th>Notes</th>
          </tr>
          <tr
            v-for="complication in dialysis_complications"
            :key="complication.id"
          >
            <td>{{ complication.time_lapsed }}</td>
            <td>
              <span v-for="com in complication.complications" :key="com">
                {{ com + ", " }}
              </span>
            </td>
            <td>{{ complication.notes }}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <h3>Consumables Used</h3>
        <table>
          <tr>
            <th>#id</th>
            <th>Item</th>
            <th>Quantity</th>
          </tr>
          <tr v-for="consumed in dialysis_consumables_used" :key="consumed.id">
            <td>{{ consumed.id }}</td>
            <td>{{ consumed.item_name }}</td>
            <td>{{ consumed.quantity }}</td>
          </tr>
        </table>
      </div>

      <div v-if="dialysis_end_session_data" class="section">
        <h3>End of Session Data</h3>
        <p>
          <strong>Post-Dialysis Weight:</strong>
          {{ dialysis_end_session_data?.post_weight }} kg
        </p>
        <p>
          <strong>Total Fluid Removed:</strong>
          {{ dialysis_end_session_data?.total_removed }} ml
        </p>
        <p>
          <strong>Session Time:</strong>
          {{
            formatDateTime(session_data?.start_time) +
            " - " +
            formatDateTime(session_data?.end_time)
          }}
        </p>
        <p>
          <strong>Elapsed Time:</strong>
          {{ dialysis_end_session_data?.elapsed_time }} min
        </p>
        <p>
          <strong>Remaining Time:</strong>
          {{ dialysis_end_session_data?.remaining_time }} min
        </p>
        <p>
          <strong>Complications:</strong>
          {{ dialysis_end_session_data?.complications }} min
        </p>
        <p>
          <strong>Actions Taken:</strong>
          {{ dialysis_end_session_data?.actions }}
        </p>
        <p>
          <strong>Medications Administered:</strong>
          {{ dialysis_end_session_data?.medications }}
        </p>
        <p>
          <strong>Machine Disinfection Cycle Confirmed:</strong>
          {{ dialysis_end_session_data?.disinfection_done }}
        </p>
      </div>
    </div>

    <!-- end all data presentation -->
  </div>
  <!--  -->
</template>
<script>
import { generalApiRequest } from "../../../api/apiIndex";
import formatDateTime from "../../../timezone/utc_local_time";

export default {
  name: "DialysisTreatment",
  data() {
    return {
      sessionStarted: false,
      sessionEnded: false,
      triage: [],
      patient: {},
      elapsedTime: 0, // in seconds
      timer: null,
      sessionStartTime: "",
      fluid_balance: [],
      consumablesUsed: ["Dialyser FX80", "Heparin 5000 IU", "Saline 500ml"],
      session_data: {},
      dialysis_machine_parameters: [],
      dialysis_complications: [],
      dialysis_consumables_used: [],
      dialysis_end_session_data: {},

      insearch_visit: this.$route.params.visitid,
    };
  },
  props: {
    procedure_data: {
      type: Object,
      required: true,
    },
  },

  methods: {
    formatDateTime,
    async get_patient_n_visit() {
      const url = "get_patient_object_from_visit/" + this.insearch_visit;
      const resp = await generalApiRequest(url, "GET", {}, 1);
      this.patient = resp.data;
    },

    async get_session_details() {
      try {
        const url = "get_dialysis_session/" + this.insearch_visit;
        const resp = await generalApiRequest(url, "GET", {}, 1);

        console.log(resp);

        this.session_data = resp.data;
        this.triage = resp.triage;
        this.fluid_balance = resp.fluid_balance;
        this.dialysis_machine_parameters = resp.dialysis_machine_parameters;
        this.dialysis_complications = resp.dialysis_complications;
        this.dialysis_consumables_used = resp.dialysis_consumables_used;
        this.dialysis_end_session_data = resp.dialysis_end_session_data;

        // If session has start time, resume its timer
        if (resp && this.session_data.start_time) {
          this.sessionStarted = true;

          // Extract HH:mm:ss
          this.sessionStartTime = this.session_data.start_time.split(" ")[1];

          // Calculate elapsed time
          const start = new Date(
            this.session_data.start_time.replace(" ", "T")
          );
          const now = new Date();
          const elapsedMs = now - start;
          this.elapsedTime = Math.floor(elapsedMs / 1000);

          // Clear old timer
          if (this.timer) {
            clearInterval(this.timer);
          }

          // Start counter
          this.timer = setInterval(() => {
            this.elapsedTime++;
          }, 1000);
        } else {
          this.sessionStarted = false;
          this.sessionStartTime = null;
          this.elapsedTime = 0;
        }
      } catch (error) {
        console.log(error);
      }
    },

    formatTime(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${this.pad(hrs)}:${this.pad(mins)}:${this.pad(secs)}`;
    },

    pad(val) {
      return val.toString().padStart(2, "0");
    },
  },

  mounted() {
    // console.log("this is -------------");
    // console.log(this.procedure_data);

    this.get_patient_n_visit();
    this.get_session_details();
  },
};
</script>

<style scoped>
.dialysis-page {
  max-width: 1200px;
  margin: 30px auto;
  padding: 20px;
  background: #fff;
  border: 1px solid #ccc;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.header {
  text-align: center;
  margin-bottom: 20px;
}

.status {
  font-size: 18px;
  font-weight: 600;
  margin-top: 5px;
}
.status.pending {
  color: #999;
}
.status.ongoing {
  color: #007bff;
}
.status.ended {
  color: #28a745;
}

h2,
h3,
h4 {
  margin-bottom: 10px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}
.patient-info {
  background-color: #fbf8ef;
}

.progress-bar {
  width: 100%;
  background: #e4e4e4;
  height: 25px;
  border-radius: 5px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: #4caf50;
  transition: width 0.4s ease;
}

.time-info {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
}

.btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.btn {
  padding: 10px 16px;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.start {
  background: #28a745;
}
.stop {
  background: #dc3545;
}
.vitals {
  background: #007bff;
}
.green {
  background: #17a2b8;
}
.orange {
  background: #fd7e14;
}

.controls {
  text-align: center;
  margin-bottom: 20px;
}

.summary-section ul {
  padding-left: 20px;
  list-style: disc;
  margin-bottom: 10px;
}

/* start style for data presentation */

.session-summary {
  font-family: Arial, sans-serif;
  color: #333;
  /*  padding: 1.5rem;
  background: #fdfdfd;
  border: 1px solid #ddd;
  border-right: 1.5em solid #ddd;
  border-radius: 0.5rem;
  margin: 1.5rem auto;
  max-width: 1000px; */
}

.session-summary h2 {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  border-bottom: 2px solid #444;
  padding-bottom: 0.4rem;
}

.session-summary .section {
  margin-bottom: 1.5rem;
}

.session-summary h3 {
  font-size: 1.2rem;
  margin-bottom: 0.6rem;
  color: #222;
  border-bottom: 1px solid #ccc;
  padding-bottom: 0.3rem;
}

.session-summary p {
  margin: 0.3rem 0;
}

.session-summary table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.session-summary th,
.session-summary td {
  border: 1px solid #ddd;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.95rem;
}

.session-summary th {
  background: #f2f2f2;
  font-weight: 600;
}

.session-summary tr:nth-child(even) {
  background: #fafafa;
}

.session-summary tr:hover {
  background: #f1f7fd;
}
/* ends style for data presentation */
</style>
